import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import InfoPopover from '@/components/common/InfoPopover';
import UserTalentsSection from '@/components/settings/user-dialog/UserTalentsSection';
import { Talent } from '../../services/api/types';
import { useMyTalents } from '../../hooks/useMyTalents';
import { useAuth } from '../../contexts/AuthContext';

interface MyTalentsCardProps {
  userId: number;
}

const MyTalentsCard: React.FC<MyTalentsCardProps> = ({ userId }) => {
  const { userTalents, allTalents, loading, add, update, remove } = useMyTalents(userId);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin } = useAuth();

  const handleAddTalent = async (talent: Talent) => {
    try {
      setError(null);
      await add(talent);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke lagre talent');
    }
  };

  const handleUpdateTalentDetail = async (
    talentId: number,
    field: 'erfaringsnivaa' | 'notater',
    value: any
  ) => {
    try {
      setError(null);
      await update(talentId, field, value);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke lagre talent');
    }
  };

  const handleRemoveTalent = async (talentId: number) => {
    try {
      setError(null);
      await remove(talentId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke lagre talent');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Mine talenter
          <InfoPopover label="Hva er mine talenter?">
            <p>
              Lister alle talenter du er registrert med, og som du kan tildeles i en
              produksjon.
            </p>
            <p>
              Talenter du er satt opp med i en produksjon kan ikke fjernes, men du kan alltid
              endre nivået.
            </p>
          </InfoPopover>
        </CardTitle>
        <CardDescription>
          Talentene dine brukes når du settes opp på produksjoner.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <UserTalentsSection
          userTalents={userTalents}
          allTalents={allTalents}
          onAddTalent={handleAddTalent}
          onUpdateTalentDetail={handleUpdateTalentDetail}
          onRemoveTalent={handleRemoveTalent}
          loading={loading}
          lockTalentsInProduction={!isAdmin()}
        />
      </CardContent>
    </Card>
  );
};

export default MyTalentsCard;
